import { useState, useCallback } from "react";
import { EMAILJS_CONFIG } from "../config/emailjs";

/**
 * @typedef {'idle' | 'sending' | 'sent' | 'error'} FormStatus
 */

/**
 * @typedef {Object} FormFields
 * @property {string} name
 * @property {string} email
 * @property {string} phone
 * @property {string} message
 */

const INITIAL_FIELDS = /** @type {FormFields} */ ({
  name: "", email: "", phone: "", message: "",
});

/**
 * Encapsulates contact form state and EmailJS submission.
 * The component becomes a pure view — no side-effects inline.
 *
 * @returns {{
 *   fields: FormFields,
 *   status: FormStatus,
 *   handleFieldChange: (key: keyof FormFields) => (e: React.ChangeEvent) => void,
 *   handleSubmit: (e: React.FormEvent) => Promise<void>,
 * }}
 */
export function useContactForm() {
  const [fields, setFields] = useState(INITIAL_FIELDS);
  const [status, setStatus] = useState(/** @type {FormStatus} */ ("idle"));

  const handleFieldChange = useCallback(
    (key) => (e) => setFields((prev) => ({ ...prev, [key]: e.target.value })),
    []
  );

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setStatus("sending");

    try {
      const emailjs = await import("@emailjs/browser");
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        {
          from_name:  fields.name.trim(),
          from_email: fields.email.trim(),
          phone:      fields.phone.trim() || "—",
          message:    fields.message.trim(),
          to_email:   EMAILJS_CONFIG.toEmail,
        },
        EMAILJS_CONFIG.publicKey
      );
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }, [fields]);

  return { fields, status, handleFieldChange, handleSubmit };
}
